/**
 * The dashboard and API share an origin in production, so requests are
 * same-site and the session cookie travels on its own. `credentials: include`
 * is still explicit because the Vite dev proxy is a different port.
 */
import type { RoundingRule } from "./rounding";
export type FieldIssue = { field: string; message: string };

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly issues: FieldIssue[];

  constructor(status: number, code: string, message: string, issues: FieldIssue[] = []) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.issues = issues;
  }

  /** Message for a specific form field, if the API blamed one. */
  fieldError(field: string): string | undefined {
    return this.issues.find((i) => i.field === field)?.message;
  }
}

/**
 * Last ETag seen per polled endpoint.
 *
 * Sending it back means an unchanged poll costs one indexed aggregate on the
 * server and no body on the wire, instead of the full query every few seconds
 * for every open dashboard.
 */
const etags = new Map<string, string>();

/** Returned when the server confirms nothing has changed. */
export const NOT_MODIFIED = Symbol("not-modified");
export type Polled<T> = T | typeof NOT_MODIFIED;

async function pollableRequest<T>(path: string, fresh = false): Promise<Polled<T>> {
  // `fresh` is for when the caller is showing something else and needs this
  // answer in full: a "not modified" would leave the wrong list on screen.
  if (fresh) etags.delete(path);
  const known = etags.get(path);
  const res = await fetch(path, {
    credentials: "include",
    headers: known ? { "if-none-match": known } : {},
  }).catch(() => null);

  if (!res) throw new ApiError(0, "network_error", "Can't reach the server. Check your connection.");
  if (res.status === 304) return NOT_MODIFIED;

  const tag = res.headers.get("etag");
  if (tag) etags.set(path, tag);
  else etags.delete(path);

  const body = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(res.status, body?.error ?? "request_failed", body?.message ?? "Something went wrong.");
  }
  return body as T;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Only declare a JSON content-type when there is actually a body. Fastify
  // rejects an empty body that claims to be JSON, which breaks every bodyless
  // POST and DELETE — logout and session revocation included.
  const headers: Record<string, string> = { ...(init.headers as Record<string, string> | undefined) };
  // (An upload sets its own type, so only fill in JSON when nothing was chosen.)
  if (init.body !== undefined && !headers["content-type"]) headers["content-type"] = "application/json";

  let res: Response;
  try {
    res = await fetch(path, { ...init, credentials: "include", headers });
  } catch {
    // A network failure is not the same as a rejected request, and saying so
    // stops people retyping a password that was never actually wrong.
    throw new ApiError(0, "network_error", "Can't reach the server. Check your connection.");
  }

  if (res.status === 204) return undefined as T;

  const body = await res.json().catch(() => null);

  if (!res.ok) {
    throw new ApiError(
      res.status,
      body?.error ?? "request_failed",
      body?.message ?? "Something went wrong.",
      body?.issues ?? [],
    );
  }

  return body as T;
}

export type User = { id: string; email: string; role: "admin" | "viewer" };
export type Organization = {
  id: string;
  name: string;
  /** Unit fuel is shown in across the dashboard and its PDFs; null = each mission's own. */
  reportFuelUnit?: FuelUnit | null;
};
export type Me = { user: User; organization: Organization };

export type Pilot = {
  id: string;
  displayName: string;
  /** Set once archived: hidden from iPads, still on every sheet it flew. */
  archivedAt: string | null;
  createdAt: string;
  missionCount: number;
};

export type SessionRow = {
  id: string;
  kind: "web" | "device";
  label: string | null;
  userAgent: string | null;
  ipAddress: string | null;
  lastSeenAt: string;
  createdAt: string;
  current: boolean;
};

export const FUEL_UNITS = ["litres", "gallons", "pounds", "kilograms"] as const;
export const LOAD_UNITS = ["litres", "gallons", "kilograms", "pounds"] as const;
export const CATEGORIES = ["helicopter", "fixed_wing"] as const;
export const STATUSES = ["serviceable", "unserviceable", "retired"] as const;

export type FuelUnit = (typeof FUEL_UNITS)[number];
export type LoadUnit = (typeof LOAD_UNITS)[number];
export type Category = (typeof CATEGORIES)[number];
export type Status = (typeof STATUSES)[number];

export type Aircraft = {
  id: string;
  registration: string;
  callsign: string | null;
  model: string | null;
  category: Category;
  fuelUnit: FuelUnit;
  fuelCapacity: string | null;
  loadCapacity: string | null;
  loadUnit: LoadUnit | null;
  status: Status;
  notes: string | null;
  lastLatitude: number | null;
  lastLongitude: number | null;
  lastSuburb: string | null;
  lastPositionAt: string | null;
  /** Only on the fleet list; create and update responses leave it out. */
  missionCount?: number;
};

export type AircraftInput = {
  registration: string;
  callsign?: string;
  model?: string;
  category: Category;
  fuelUnit: FuelUnit;
  fuelCapacity?: string;
  loadCapacity?: string;
  loadUnit?: LoadUnit;
  status: Status;
  notes?: string;
};

/** What an organisation's iPads log and how its sheets report. Mirrors the server's. */
export type OdrConfig = {
  ipad: {
    fuelPrompts: { atEngineStart: boolean; atEngineShutdown: boolean };
    showCooldownTimer: boolean;
  };
  report: { engineTime: RoundingRule; flightTime: RoundingRule };
};

export type OdrState = {
  published: { version: number; config: OdrConfig; publishedAt: string } | null;
  draft: { config: OdrConfig; updatedAt: string } | null;
  hasUnpublishedChanges: boolean;
  defaults: OdrConfig;
};

/** Who the company is on its exported sheets, and how its reports show fuel. */
export type OrgProfile = {
  reportFuelUnit: FuelUnit | null;
  name: string;
  abn: string | null;
  hasLogo: boolean;
  logoUpdatedAt: string | null;
};

export type MissionRow = {
  id: string;
  missionName: string;
  aircraftRegistration: string | null;
  aircraftCallsign: string | null;
  pilotName: string | null;
  task: string | null;
  startedAt: string;
  endedAt: string | null;
  currentPhase: string;
  fuelUnit: FuelUnit;
  startingFuel: string | null;
  endFuel: string | null;
  receivedAt: string;
  airTimeSeconds: number;
  engineTimeSeconds: number;
  dropCount: number;
  legCount: number;
  engineStartCount: number;
  fuelUplifted: string | null;
  fuelBurnt: string | null;
  /**
   * The hours as reported on the sheet, after the organisation's rounding rule.
   * The exact seconds above are never rounded; these are what to display.
   */
  reportedEngineMinutes: number;
  reportedAirMinutes: number;
  reportedEngineDp: number;
  reportedAirDp: number;
};

export type MissionDetail = MissionRow & {
  companyName: string | null;
  aircraftId: string | null;
};

export type MissionEvent = {
  id: string;
  sequence: number;
  recordedAt: string;
  event: string;
  phase: string;
  refuelAmount: string | null;
  refuelSupplier: string | null;
  fuelLevel: string | null;
  carriedFuelLevel: string | null;
  latitude: number | null;
  longitude: number | null;
  altitude: number | null;
  suburb: string | null;
};

export type OverviewFlying = {
  id: string;
  missionName: string;
  aircraftId: string | null;
  registration: string | null;
  callsign: string | null;
  pilotName: string | null;
  phase: string;
  startedAt: string;
  lastContactAt: string;
  dropCount: number;
  airTimeSeconds: number;
};

export type OverviewAttention = {
  kind: "aircraft_unserviceable" | "mission_left_open" | "mission_unmatched" | "device_quiet";
  title: string;
  detail: string;
  link: { to: "mission"; id: string } | { to: "fleet" } | { to: "devices" };
};

export type Overview = {
  generatedAt: string;
  staleAfterMinutes: number;
  flying: OverviewFlying[];
  attention: OverviewAttention[];
  /** What a new organisation has done so far. */
  setup: { aircraft: boolean; device: boolean; mission: boolean; odr: boolean; company: boolean };
  last24h: {
    missions: number;
    airTimeSeconds: number;
    /** As the sheets report it, after any rounding. */
    flightMinutes: number;
    flightDp: number;
    dropCount: number;
    fuelUsed: number;
    fuelUnit: FuelUnit;
  };
  fleet: {
    id: string;
    registration: string;
    callsign: string | null;
    model: string | null;
    status: Status;
    flyingMissionId: string | null;
    lastPosition: { latitude: number | null; longitude: number | null; suburb: string | null; at: string } | null;
  }[];
};

export const api = {
  getOverview: () => request<Overview>("/v1/overview"),

  signup: (input: {
    orgName: string;
    displayName?: string;
    email: string;
    password: string;
    confirmPassword: string;
  }) => request<Me & { token: string }>("/v1/auth/signup", { method: "POST", body: JSON.stringify(input) }),

  login: (input: { email: string; password: string }) =>
    request<{ user: User; token: string }>("/v1/auth/login", { method: "POST", body: JSON.stringify(input) }),

  logout: () => request<void>("/v1/auth/logout", { method: "POST" }),

  me: () => request<Me>("/v1/auth/me"),

  sessions: () => request<{ sessions: SessionRow[] }>("/v1/auth/sessions"),

  revokeSession: (id: string) => request<void>(`/v1/auth/sessions/${id}`, { method: "DELETE" }),

  listAircraft: () => request<{ aircraft: Aircraft[] }>("/v1/aircraft"),

  createAircraft: (input: AircraftInput) =>
    request<{ aircraft: Aircraft }>("/v1/aircraft", { method: "POST", body: JSON.stringify(input) }),

  updateAircraft: (id: string, input: Partial<AircraftInput>) =>
    request<{ aircraft: Aircraft }>(`/v1/aircraft/${id}`, { method: "PATCH", body: JSON.stringify(input) }),

  retireAircraft: (id: string) =>
    request<{ aircraft: Aircraft }>(`/v1/aircraft/${id}`, { method: "DELETE" }),

  listPilots: () =>
    request<{ pilots: Pilot[]; unlinkedNames: { name: string; missionCount: number }[] }>("/v1/pilots"),

  createPilot: (displayName: string) =>
    request<{ pilot: Pilot }>("/v1/pilots", { method: "POST", body: JSON.stringify({ displayName }) }),

  updatePilot: (id: string, input: { displayName?: string; archived?: boolean }) =>
    request<{ pilot: Pilot }>(`/v1/pilots/${id}`, { method: "PATCH", body: JSON.stringify(input) }),

  /** `query` is a ready query string ("" or "?q=…"), shared with the CSV link so both always agree. */
  listMissions: (query = "", fresh = false) =>
    pollableRequest<{ missions: MissionRow[]; hasMore: boolean }>(`/v1/missions${query}`, fresh),

  getOrgProfile: () => request<OrgProfile>("/v1/org/settings"),

  /** Any subset of the settings; the server answers with the whole profile. */
  updateOrgSettings: (input: { reportFuelUnit?: FuelUnit | null; name?: string; abn?: string | null }) =>
    request<OrgProfile>("/v1/org/settings", { method: "PATCH", body: JSON.stringify(input) }),

  /** The raw image is the request body: no multipart, nothing to parse. */
  uploadLogo: (file: File) =>
    request<{ hasLogo: true; width: number; height: number; updatedAt: string }>("/v1/org/logo", {
      method: "PUT",
      body: file,
      headers: { "content-type": file.type },
    }),

  removeLogo: () => request<void>("/v1/org/logo", { method: "DELETE" }),

  /**
   * Where the mission's Daily Flight Summary downloads from. The viewer's own
   * timezone and locale are passed so clock times print as they would on an
   * iPad in the same place — the server runs on UTC and cannot know.
   */
  sheetUrl: (id: string, fuelUnit?: FuelUnit) => {
    const q = new URLSearchParams({
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      locale: navigator.language || "en-AU",
    });
    if (fuelUnit) q.set("fuelUnit", fuelUnit);
    return `/v1/missions/${id}/sheet.pdf?${q}`;
  },

  getMission: (id: string) =>
    pollableRequest<{
      mission: MissionDetail;
      events: MissionEvent[];
      /** The rule this mission reports under. Absent from responses cached before ODRs. */
      report?: { engineTime: RoundingRule; flightTime: RoundingRule };
    }>(`/v1/missions/${id}`),

  getOdr: () => request<OdrState>("/v1/odr"),

  saveOdrDraft: (config: OdrConfig) =>
    request<{ draft: { config: OdrConfig; updatedAt: string } }>("/v1/odr/draft", {
      method: "PUT",
      body: JSON.stringify({ config }),
    }),

  publishOdr: () =>
    request<{ published: { version: number; config: OdrConfig; publishedAt: string } }>("/v1/odr/publish", {
      method: "POST",
    }),
};
